import { Component, useEffect, useRef, type ReactNode } from "react";
import { useRouteError } from "react-router-dom";
import { Logo, Button } from "@/design/components";
import styles from "./ErrorBoundary.module.css";

function reloadPage() {
  window.location.reload();
}

/**
 * Branded full-screen fallback for an unrecoverable render/runtime error.
 * Presentational only — shared by the route-level {@link RouteError} (React
 * Router catches route render/loader errors) and the app-wide
 * {@link AppErrorBoundary} (catches anything above/outside the router).
 */
export function ErrorFallback() {
  const ref = useRef<HTMLDivElement>(null);
  // Move focus to the fallback on mount: a role="alert" region inserted already
  // populated is announced inconsistently by AT (WCAG 4.1.3), and after the broken
  // subtree is replaced keyboard focus would otherwise be stranded. Focusing the
  // container makes the context switch reliably announced and reachable.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div className={styles.shell} role="alert" ref={ref} tabIndex={-1}>
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.inner}>
        <Logo size={30} />
        <p className="u-eyebrow">Unexpected error</p>
        <h1 className={styles.title}>Something went off-script.</h1>
        <p className={styles.lede}>
          The page hit an error it couldn't recover from. Your data is safe — reloading usually
          clears it. If it keeps happening, contact{" "}
          <a href="mailto:support@essentialai.uk">support@essentialai.uk</a>.
        </p>
        <div className={styles.actions}>
          <Button size="lg" onClick={reloadPage}>
            Reload the page
          </Button>
          {/* Full nav (not router link) so it also resets a broken React tree. */}
          <a className={styles.alt} href="/login">
            Back to sign in
          </a>
        </div>
      </div>
    </div>
  );
}

/** Route-level error element: React Router renders route render/loader errors here. */
export function RouteError() {
  const error = useRouteError();
  console.error("[route-error]", error);
  return <ErrorFallback />;
}

interface BoundaryState {
  hasError: boolean;
}

/**
 * App-wide class error boundary. React Router routes route-tree errors to
 * `errorElement` (RouteError); this catches the rest — provider render errors,
 * or a failure in RouterProvider itself — so a single throw can never leave the
 * user staring at a blank white page.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error("[app-error-boundary]", error, info);
  }

  render() {
    return this.state.hasError ? <ErrorFallback /> : this.props.children;
  }
}
